import axios from 'axios'
import { getRefreshToken, getToken, isLoggedIn, logout, setToken } from './browser'
import { API_TEMPLATE } from './constant'
// import { toJson } from './util'
import { message } from 'antd'

message.config({
  top: 30,
  maxCount: 1
})
let refreshRequest = null

const instance = axios.create({
  baseURL: process.env.API_ENDPOINT || '/'
})
instance.defaults.headers.common['Accept-Language'] = 'vi'
instance.interceptors.response.use((response) => {
  return response
}, async (error) => {
  const originalRequest = error.config
  console.log('error.config', JSON.stringify(error.config));

  if (error?.response?.status === 401 && !originalRequest._retry && isLoggedIn()) {
    originalRequest._retry = true
    const refreshToken = getRefreshToken()
    console.log('refreshToken', refreshToken);
    if (refreshToken) {
      try {
        refreshRequest = refreshRequest || instance.post(API_TEMPLATE.AUTH_REFRESH_TOKEN, { refreshToken })
        const { data } = await refreshRequest
        refreshRequest = null
        setToken(data)
        originalRequest.headers['Authorization'] = getToken()
        return instance(originalRequest)
      } catch {
        refreshRequest = null
        logoutAndgoToLogin()
        return Promise.reject(error)
      }
    } else {
      logoutAndgoToLogin()
      return Promise.reject(error)
    }
  }

  if (error?.response?.status >= 400 && error?.response?.status !== 404 && error?.response?.status !== 401) {
    const errorMessage = `Code: ${error.response.status}, ${error.response.data?.message || 'Lỗi xảy ra, vui lòng thử lại'}`
    message.error(errorMessage)
  }
  throw error
})

/**
 * Send a DELETE request to the given path using the configured axios instance.
 * Uses the Authorization header from `getConfig()`.
 *
 * @param {string} path - Request path or full URL.
 * @returns {Promise<any>} Resolves with the normalized response object or an error object.
 */
export function callDelete (path) {
  // console.debug(`libs/query - callDelete - path: ${path}`)

  return instance.delete(path, getConfig()).then(res => {
    return handleResponse(res)
  }).catch(error => {
    return handleError(error)
  })
}

/**
 * Send a GET request with optional query parameters. Returned promise has a
 * `.cancel` method to abort the request.
 *
 * @param {string} path - Request path or full URL.
 * @param {object} [params={}] - Query parameters to include in the request.
 * @returns {Promise<any> & {cancel: Function}} Resolves with the normalized response object or an error object.
 */
export function get (path, params = {}) {
  // console.debug(`libs/query - get - path: ${path}`)
  const { cancel, cancelToken } = handleCancelRequest()
  const promise = instance.get(path, { ...getConfig(), cancelToken, params }).then(res => {
    return handleResponse(res)
  }).catch(error => {
    return handleError(error)
  })
  promise.cancel = cancel
  return promise
}

/**
 * Send a POST request with request body. You can pass an axios config object
 * which will be merged with the default config (including Authorization header).
 * The returned promise has a `.cancel` method to abort the request.
 *
 * @param {string} path - Request path or full URL.
 * @param {any} data - Request body payload.
 * @param {import('axios').AxiosRequestConfig} [config={}] - Optional axios request config.
 * @returns {Promise<any> & {cancel: Function}} Resolves with the normalized response object or an error object.
 */
export function post (path, data, config = {}) {
  // console.debug(`libs/query - post - path: ${path}, data: ${toJson(data)}`)
  const { cancel, cancelToken } = handleCancelRequest()
  const promise = instance.post(path, data, { ...config, ...getConfig(), cancelToken }).then(res => {
    return handleResponse(res)
  }).catch(error => {
    return handleError(error)
  })
  promise.cancel = cancel
  return promise
}

/**
 * Send a POST request without attaching the Authorization token.
 * Useful for endpoints like login/refresh that don't require the current token.
 *
 * @param {string} path - Request path or full URL.
 * @param {any} data - Request body payload.
 * @returns {Promise<any>} Resolves with the normalized response object or an error object.
 */
export function postWithoutToken (path, data) {
  // console.debug(`libs/query - post - path: ${path}, data: ${toJson(data)}`)

  return instance.post(path, data).then(res => {
    return handleResponse(res)
  }).catch(error => {
    return handleError(error)
  })
}

/**
 * Send a PATCH request with request body using the configured axios instance.
 *
 * @param {string} path - Request path or full URL.
 * @param {any} data - Request body payload.
 * @returns {Promise<any>} Resolves with the normalized response object or an error object.
 */
export function patch (path, data) {
  // console.debug(`libs/query - patch - path: ${path}, data: ${toJson(data)}`)

  return instance.patch(path, data, getConfig()).then(res => {
    return handleResponse(res)
  }).catch(error => {
    return handleError(error)
  })
}

/**
 * Send a PUT request with request body. Optional axios config will be merged
 * with the default config (including Authorization header).
 *
 * @param {string} path - Request path or full URL.
 * @param {any} data - Request body payload.
 * @param {import('axios').AxiosRequestConfig} [config] - Optional axios request config.
 * @returns {Promise<any>} Resolves with the normalized response object or an error object.
 */
export function put (path, data, config) {
  // console.debug(`libs/query - put - path: ${path}, data: ${toJson(data)}`)

  return instance.put(path, data, { ...config, ...getConfig() }).then(res => {
    return handleResponse(res)
  }).catch(error => {
    return handleError(error)
  })
}

/**
 * Upload multipart/form-data to the given path. Attaches Authorization token
 * in the request headers and sets Content-Type to multipart/form-data.
 *
 * @param {string} path - Request path or full URL.
 * @param {FormData} form - FormData instance containing file(s) and fields.
 * @returns {Promise<any>} Resolves with the normalized response object or an error object.
 */
export function upload (path, form) {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: getToken()
    }
  }
  return instance.post(path, form, config).then(res => {
    return handleResponse(res)
  }).catch(error => {
    return handleError(error)
  })
}

function handleError (error) {
  // console.debug(`libs/query - handleError - error: ${toJson(error)}`)
  if (error.response) {
    // The request was made and the server responded with a status code that falls out of the range of 2xx
    // console.debug(`libs/query - handleError - error data: ${toJson(error.response.data)}`)
    // console.debug(`libs/query - handleError - error status: ${toJson(error.response.status)}`)
    // console.debug(`libs/query - handleError - error headers: ${toJson(error.response.headers)}`)

    if (error.response.status >= 400) {
      // console.debug(`libs/query - handleError - error message: ${error.response.data.message}`)
      return { status: error.response.status, code: error.response.data.code, data: error.response.data.message }
    }
    return { status: error.response.status }
  } else if (error.request) {
    // The request was made but no response was received
    // console.debug(`libs/query - handleError - error request: ${toJson(error.request)}`)
  } else {
    // Something happened in setting up the request that triggered an Error
    // console.debug(`libs/query - handleError - error setting up the request: ${toJson(error.message)}`)
  }

  return { code: 999 }
}

/**
 * Normalize axios responses to a consistent shape used across the app.
 *
 * @param {import('axios').AxiosResponse} response - Axios response object.
 * @returns {{code: string|number, data: any, status: number}} Normalized response object.
 */
function handleResponse (response) {
  let code = ''
  let data
  if (response.status === 200) {
    data = response.data
  }

  if (response.status === 400) {
    code = response.data.code
    data = response.data.message || 'Bad request'
  }

  if (response.status === 403) {
    data = 'No permission'
  }

  if (response.status >= 500) {
    data = 'Internal Server Error'
  }

  // console.debug(`libs/query - handleResponse - code: ${code}, status: ${response.status}`)
  return {
    code,
    data,
    status: response.status
  }
}

/**
 * Build default axios request config including Authorization header from browser helper.
 *
 * @returns {{headers: {Authorization: string}}} Axios request config with headers.
 */
function getConfig () {
  return {
    headers: {
      Authorization: getToken()
    }
  }
}

/**
 * Create an axios cancel token and a convenient cancel function for callers.
 *
 * @returns {{cancelToken: import('axios').CancelToken, cancel: Function}} An object containing the cancelToken and a cancel() function.
 */
function handleCancelRequest () {
  const source = axios.CancelToken.source()
  const cancelToken = source.token
  return {
    cancelToken,
    cancel: () => {
      source.cancel('cancel request')
    }
  }
}

/**
 * Logout the user and redirect to the login page, preserving the current location
 * in the `from` query parameter.
 *
 * @returns {void}
 */
const logoutAndgoToLogin = () => {
  const currentUrl = window.location.pathname + window.location.search
  logout()
  window.location.replace(`/login?from=${encodeURIComponent(currentUrl)}`)
}
